import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import { Editor } from '@tinymce/tinymce-react';
import { fetchTopics, fetchThread, updateThread } from '../services/api';
import { useInstitution } from './InstitutionContext';

export default function EditThreadDialog({ open, threadId, onClose, onUpdated, onSuccess }) {
  const { selectedInstitution } = useInstitution();
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [subtopicId, setSubtopicId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    const loadTopics = async () => {
      if (!selectedInstitution) {
        setTopics([]);
        return;
      }
      try {
        const data = await fetchTopics(selectedInstitution);
        setTopics(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load topics', err);
      }
    };
    loadTopics();
  }, [open, selectedInstitution]);

  useEffect(() => {
    if (!open || !threadId) return;
    const loadThread = async () => {
      try {
        const data = await fetchThread(threadId);
        const thread = data.thread || data;
        setTitle(thread.title || '');
        setContent(thread.content || '');
        setTopicId(thread.topic?.id || '');
        setSubtopicId(thread.subtopic?.id || '');
      } catch (err) {
        console.error('Failed to load thread', err);
      }
    };
    loadThread();
  }, [open, threadId]);

  const handleSave = async () => {
    try {
      const payload = {
        title,
        content,
        topic: topicId,
        subtopic: subtopicId || null,
      };
      const updated = await updateThread(threadId, payload);
      if (onUpdated) onUpdated(updated);
      const msg = 'Post updated';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
    } catch (err) {
      console.error('Failed to update thread', err);
    }
  };

  const selectedTopic = topics.find(t => String(t.id) === String(topicId));
  const subtopics = selectedTopic?.subtopics || [];

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Thread</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
            <Typography fontWeight="bold">Topic</Typography>
            <TextField
              select
              value={topicId}
              onChange={(e) => {
                setTopicId(e.target.value);
                setSubtopicId('');
              }}
              fullWidth
            >
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
            <Typography fontWeight="bold">Subtopic</Typography>
            <TextField
              select
              value={subtopicId}
              onChange={(e) => setSubtopicId(e.target.value)}
              fullWidth
              disabled={!subtopics.length}
            >
              {subtopics.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography fontWeight="bold">Title</Typography>
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
        </Box>
        <Typography fontWeight="bold">Content</Typography>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          value={content}
          init={{
            license_key: 'gpl',
            height: 300,
            menubar: false,
            plugins: ['link', 'lists', 'autolink', 'charmap', 'table'],
            toolbar: 'undo redo | bold italic underline | bullist numlist outdent indent',
          }}
          onEditorChange={(c) => setContent(c)}
        />
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="success" />
        <FilledButton onClick={handleSave} text="Save" color="success" />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
}
